<script setup lang="ts">
/**
 * Card header de la page événement : badge catégorie, titre, organisation,
 * bouton Partager, date + heure, description expandable.
 */
import type { Event } from "~/types/event";
import { themeAffiche, typeActiviteAffiche } from "~/utils/themesAffiches";

const props = defineProps<{
  event: Event;
  formattedDate: string;
  formattedEnd: string | null;
  descriptionHtml: string;
}>();

defineEmits<{
  (e: 'open-share'): void;
}>();

const { t } = useI18n();
const localePath = useLocalePath();

/** Page de l'association, dans la langue du visiteur (null sans adresse connue). */
const lienAssociation = computed(() =>
  props.event.organization_slug
    ? localePath({ path: `/associations/${props.event.organization_slug}` })
    : null,
);

// Pastille : le type d'activité (« Concerts ») s'il est précisé, sinon le
// thème (« Musique ») — liste commune, 2026-09-26.
const pastille = computed(() => {
  const e = props.event as Event & { activity_type?: string | null };
  if (e.activity_type && e.activity_type !== "autre") return typeActiviteAffiche(e.activity_type);
  if (e.category && e.category !== "autre") return themeAffiche(e.category);
  return "";
});</script>

<template>
  <div class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] p-4 md:p-6">
    <div class="flex items-start justify-between gap-4">
      <div class="flex-1 min-w-0">
        <div v-if="pastille" class="mb-2">
          <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-[#1e1e1e] text-[#505870] dark:text-gray-400">
            {{ pastille }}
          </span>
        </div>
        <h1 class="text-xl md:text-3xl font-bold text-[#2d2e5c] dark:text-white leading-tight">
          {{ event.title }}
        </h1>
        <i18n-t keypath="billetterie.bslugHeaderCard.parOrganisation" tag="p" class="text-sm md:text-base text-[#717198] dark:text-gray-400 mt-1">
          <template #nom>
            <!-- Lien vers la page de l'association, comme sur la cagnotte et
                 la boutique. Nouvel onglet : la commande de billets en cours
                 n'est pas perdue. Sans adresse connue, texte simple plutôt
                 qu'un lien mort. -->
            <a
              v-if="lienAssociation"
              :href="lienAssociation"
              target="_blank"
              rel="noopener"
              class="font-bold hover:underline underline-offset-2"
              :style="{ color: 'var(--accent, #212121)' }"
            >{{ event.organization_name || t('billetterie.partage.organisationParDefaut') }}</a>
            <span v-else class="font-bold" :style="{ color: 'var(--accent, #212121)' }">{{ event.organization_name || t('billetterie.partage.organisationParDefaut') }}</span>
          </template>
        </i18n-t>
      </div>
      <button
        class="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2.5 border rounded-xl text-xs md:text-sm font-semibold hover:opacity-80 transition-all"
        :style="{ borderColor: 'var(--accent, #212121)', color: 'var(--accent, #212121)' }"
        :aria-label="t('commun.actions.partager')"
        @click="$emit('open-share')"
      >
        <UIcon name="i-lucide-share-2" class="size-3.5 md:size-4" />
        <span class="hidden sm:inline">{{ t('commun.actions.partager') }}</span>
      </button>
    </div>

    <div class="flex items-start gap-2 mt-4 text-xs md:text-sm font-semibold text-[#505870] dark:text-gray-400">
      <UIcon name="i-lucide-calendar-days" class="size-4 shrink-0 mt-0.5" />
      <span>{{ formattedDate }}<span v-if="formattedEnd" class="font-normal text-[#717198]"> — {{ formattedEnd }}</span></span>
    </div>

    <PublicPageDescriptionExpandable v-if="event.description" :html="descriptionHtml" />
  </div>
</template>
